import { paymentMethodEnum } from "../Enums/RegistrationEnum";
import { IEvent } from "../interfaces/models/IEvent";
import IFirm from "../interfaces/models/IFirm";
import { IRegistration } from "../interfaces/models/IRegistration";
import { saveInvoiceHistory, sendInvoice } from "../Services/Invoice";
import { getAccountingMailingAddressForInvoice } from "./firmInvoiceAddress";
import {
  generateInvoicePDFWithLogo,
  saveInvoicePDFToBlob,
} from "./generateInvoicePDF";
import {
  calculateInvoiceAmount,
  determinePaymentStatus,
  generateInvoiceFilename,
  getInvoiceTypeForEmail,
  getPaymentStatusForHistory,
  parseAdditionalFee,
  resolveRegistrationDateSec,
} from "./invoiceCalculations";

export type SendDuePaymentReminderOutcome = "sent" | "skipped" | "failed";

export interface SendDuePaymentReminderResult {
  outcome: SendDuePaymentReminderOutcome;
  reason?: string;
}

function resolveStoredPdfUrl(storagePathOrUrl: string): string {
  if (storagePathOrUrl.startsWith("http")) {
    return storagePathOrUrl;
  }
  const storageAccount =
    process.env.REACT_APP_STORAGE_ACCOUNT_NAME ||
    process.env.REACT_APP_STORAGE_ACCOUNT ||
    "wsgfilestorageqa";
  const container =
    process.env.REACT_APP_STORAGE_ACCOUNT_CONTAINER_NAME ||
    process.env.REACT_APP_STORAGE_CONTAINER ||
    "testing";
  const cleanStoragePath = storagePathOrUrl.startsWith("/")
    ? storagePathOrUrl.substring(1)
    : storagePathOrUrl;
  return `https://${storageAccount}.blob.core.windows.net/${container}/${cleanStoragePath}`;
}

function resolvePersonName(registration: IRegistration): string {
  const fullName = [registration.personName, registration.personLastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return fullName || registration.badgeName || "Member";
}

export function getRegistrationDisplayName(
  registration: IRegistration,
): string {
  return resolvePersonName(registration);
}

function resolveAmountPaid(registration: IRegistration): number {
  const value = registration.amountPaid;
  return typeof value === "number" && !Number.isNaN(value) ? value : 0;
}

export async function sendDuePaymentReminderForRegistration(
  registration: IRegistration,
  event: IEvent,
  firm: IFirm | null | undefined,
): Promise<SendDuePaymentReminderResult> {
  if (!registration.id || !event.id || !registration.personId) {
    return {
      outcome: "skipped",
      reason: "Registration is missing required data.",
    };
  }

  const additionalFeeValue = parseAdditionalFee(registration.additionalFee);
  const totalInvoiceAmount = calculateInvoiceAmount(
    event,
    registration,
    additionalFeeValue,
  );
  const paidAmount = resolveAmountPaid(registration);
  const paid = determinePaymentStatus(totalInvoiceAmount, paidAmount);
  const personName = resolvePersonName(registration);
  const filename = generateInvoiceFilename(event, personName, paid);
  const firmName = firm?.firmName || firm?.name || registration.firmName || "";
  const email = registration.personEmail || "";
  const invoiceAddressData = getAccountingMailingAddressForInvoice(firm);
  const paymentMethod = registration.paymentMethod || ("creditCard" as const);
  const paymentMethodOptions = Object.entries(paymentMethodEnum).map(
    ([key, value]) => ({
      id: key,
      name: value,
    }),
  );

  const pdfBlob = await generateInvoicePDFWithLogo({
    event: {
      id: event.id,
      title: event.title,
      startDate: event.startDate,
      endDate: event.endDate,
      location: event.location,
      fee: event.fee,
      program: event.program,
    },
    person: {
      name: personName,
      email,
    },
    firm: firmName ? { name: firmName } : null,
    badgeName: registration.badgeName,
    guests: registration.guests,
    guestName: registration.guestName,
    guestName2: registration.guestName2,
    eventPayment: paidAmount.toString(),
    creditPayment: "0",
    amountRefunded: "0",
    additionalFee: additionalFeeValue.toString(),
    paymentMethod,
    paymentMethodOptions,
    paid,
    notes: registration.note,
    eventId: event.id,
    registrationId: registration.id,
    personId: registration.personId,
    filename,
    invoiceAddress: invoiceAddressData,
    selectedSpecialEventIds: registration.selectedSpecialEventIds ?? [],
    specialEventGuestNames: registration.specialEventGuestNames ?? {},
    registrationDate: resolveRegistrationDateSec(registration),
    isPrepaid: registration.prepaid === true,
    isHostFirm:
      registration.isHostFirm === true ||
      registration.paymentStatus === "paidSPR",
  });

  if (!pdfBlob) {
    return {
      outcome: "failed",
      reason: "Could not generate the invoice PDF.",
    };
  }

  const storagePathOrUrl = await saveInvoicePDFToBlob(
    pdfBlob,
    filename,
    registration.personId,
    event.id,
  );

  if (!storagePathOrUrl) {
    return {
      outcome: "failed",
      reason: "Could not upload the invoice file.",
    };
  }

  const savedPdfPath = resolveStoredPdfUrl(storagePathOrUrl);
  const invoiceTypeForSend = getInvoiceTypeForEmail(paid);
  const paymentStatusForHistory = getPaymentStatusForHistory(paid);
  const currency = event.fee?.currency || "USD";

  try {
    await sendInvoice({
      registrationId: registration.id,
      eventId: event.id,
      invoiceType: invoiceTypeForSend,
      attendeeEmail: email || undefined,
      currency,
      pdfPath: savedPdfPath,
      amount: totalInvoiceAmount,
      paymentStatus: paymentStatusForHistory,
      filename,
    });

    await saveInvoiceHistory({
      registrationId: registration.id,
      eventId: event.id,
      currency,
      amount: totalInvoiceAmount,
      paymentStatus: paymentStatusForHistory,
      pdfPath: savedPdfPath,
      filename,
    });
  } catch (error: unknown) {
    const message =
      (error as { response?: { data?: { error?: string; message?: string } } })
        ?.response?.data?.error ||
      (error as { response?: { data?: { message?: string } } })?.response?.data
        ?.message ||
      (error instanceof Error ? error.message : null);
    return {
      outcome: "failed",
      reason: message || "Could not send the invoice email.",
    };
  }

  return { outcome: "sent" };
}
